import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  SCREEN_PATH,
  propertyConstants,
  DEFAULT_CURRENCY_ID,
  createListingFormTypes,
  ZERO_VALUE,
  PERCENTAGE_BASE,
  DRAFT_OVERWRITE_TITLE,
  DRAFT_OVERWRITE_MESSAGE,
} from "../../../constants";
import {
  createListingSchema,
  sanitizeDraftPayload,
} from "../../../schemas";
import {
  objectToFormData,
  formatToTwoDecimalPlaces,
  customToast,
} from "../../../shared/utility";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import ConfirmationPopup from "../../../shared/components/modals/ConfirmationPopup";
import { UseNavigatingAway } from "../../../shared/hooks/use-navigating-away/UseNavigatingAway";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import {
  homeActions,
  systemActions,
  propertyActions,
} from "../../../redux/actions";
import { ERROR_TOAST } from "../../../constants/toast.constants";
import Topbar from "../../../layout/Topbar";
import Footer from "../../../layout/Footer";
import ViewTypes from "./cards/ViewTypes";
import PriceAndArea from "./cards/PriceAndArea";
import PropertyTypes from "./cards/PropertyTypes";
import AdInformation from "./cards/AdInformation";
import PropertyImages from "./cards/PropertyImages";
import ListingTypeCard from "./cards/ListingTypeCard";
import FeaturesModal from "./features-modal/FeaturesModal";
import AddressAndLocation from "./cards/AddressAndLocation";
import FeaturesAndAmenities from "./cards/FeaturesAndAmenities";

const { TYPE_DETAILS, TYPE_FEATURES } = createListingFormTypes;

function convertFieldsToNumbers(obj) {
  const { amenity } = obj;

  // Convert specified fields to numbers
  if (amenity) {
    if (amenity.kitchens !== undefined) {
      amenity.kitchens = Number(amenity.kitchens);
    }

    if (amenity.otherRooms !== undefined) {
      amenity.otherRooms = Number(amenity.otherRooms);
    }

    if (amenity.staffQuarters !== undefined) {
      amenity.staffQuarters = Number(amenity.staffQuarters);
    }

    if (amenity.storeRooms !== undefined) {
      amenity.storeRooms = Number(amenity.storeRooms);
    }
  }
  obj.area = Number(obj.area);
  obj.depositAmount = Number(obj.depositAmount);
  obj.price = Number(obj.price);
  obj.listingType = Number(obj.listingType);
  delete obj.depositPercentage;

  return obj;
}

const firstPageErrorKeys = [
  "listingType",
  "availability",
  "startDate",
  "endDate",
  "area",
  "areaMeasurementType",
  "price",
  "depositAmount",
  "title",
  "description",
  "propertyTypeIds",
  "viewTypeIds",
  "addressLine1",
  "addressLine2",
  "countryId",
  "state",
  "city",
];

const CreateListing = (props) => {
  const {
    createProperty,
    updateProperty,
    savePropertyDraft,
    getPropertyDraft,
    spinnerActions,
    propertyDetail,
    saveChanges,
    setSaveChanges,
    getPropertyDetailById,
    getAllMustHaveAmmenities,
    getAllPropertyTypes,
    getAllViewTypes,
  } = props;

  const { id } = useParams();
  const navigate = useNavigate();
  const [coverImageId, setCoverImageId] = useState();
  const [deletedImages, setDeletedImages] = useState([]);
  const [formType, setFormType] = useState(TYPE_DETAILS);
  const [coverImageIndex, setCoverImageIndex] = useState(0);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [showDraftOverwritePrompt, setShowDraftOverwritePrompt] =
    useState(false);

  const [showDialogPrompt, confirmNavigation, cancelNavigation] =
    UseNavigatingAway(saveChanges);

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(createListingSchema),
    defaultValues: {
      currencyId: DEFAULT_CURRENCY_ID,
    },
  });

  const changeFormType = async (type) => {
    if (type === TYPE_FEATURES) {
      const isValid = await methods.trigger([
        "listingType",
        "availability",
        "startDate",
        "endDate",
        "currencyId",
        "area",
        "areaMeasurementType",
        "price",
        "depositAmount",
        "title",
        "description",
        "propertyTypeIds",
        "viewTypeIds",
        "addressLine1",
        "addressLine2",
        "countryId",
        "state",
        "city",
      ]);

      if (!isValid) {
        window.scrollTo(0, 0);
        return;
      }
    }
    window.scrollTo(0, 0);
    setFormType(type);
  };

  const handleOpenFeatureModal = () => {
    setOpenFeatureModal(true);
  };

  const handleCloseFeatureModal = () => {
    setOpenFeatureModal(false);
  };

  const spinner = useCallback(
    (type) => {
      return spinnerActions.findIndex((s) => s === type);
    },
    // eslint-disable-next-line
    [spinnerActions]
  );

  const buildPropertyFormData = (data, { asUpdate = false, asDraft = false } = {}) => {
    const reqObj = asDraft
      ? sanitizeDraftPayload(data)
      : convertFieldsToNumbers(data);

    const images = reqObj.images ?? [];
    const filteredImages = images.filter((img) => !img?.url);
    const retainedImageIds = images
      .filter((img) => img?.id)
      .map((img) => img.id);
    const updatedCoverImageIndex = filteredImages.findIndex(
      (img) => images[coverImageIndex] === img
    );
    const propertyId = id || draftId;

    return objectToFormData(
      {
        ...reqObj,
        ...(asUpdate || asDraft
          ? {
              ...(propertyId ? { id: propertyId } : {}),
              deletedImageIds: deletedImages,
              images: filteredImages,
              ...(coverImageId ? { coverImageId } : {}),
              ...(asDraft ? { retainedImageIds } : {}),
            }
          : {}),
        ...(reqObj?.nearbyLocations
          ? {
              nearbyLocations: reqObj.nearbyLocations.map(
                ({ key, ...location }) => location
              ),
            }
          : {}),
      },
      updatedCoverImageIndex >= ZERO_VALUE ? updatedCoverImageIndex : ZERO_VALUE,
      images[coverImageIndex]?.url ? false : filteredImages.length > ZERO_VALUE
    );
  };

  const onSubmit = (data) => {
    setSaveChanges(false);
    const publishId = id || draftId;
    const formData = buildPropertyFormData(data, {
      asUpdate: Boolean(publishId),
    });

    if (publishId) {
      updateProperty(formData, () => {
        setDraftId(null);
        setHasDraft(false);
        navigate(SCREEN_PATH.MY_LISTING);
      });
    } else {
      createProperty(formData, () => {
        navigate(SCREEN_PATH.MY_LISTING);
      });
    }
  };

  const applyDraftToForm = (draft) => {
    const priceValue = Number(draft.price);
    const depositValue = Number(draft.depositAmount);
    const depositPercentage =
      Number.isFinite(priceValue) &&
      priceValue > ZERO_VALUE &&
      Number.isFinite(depositValue)
        ? Number(
            formatToTwoDecimalPlaces(
              (depositValue / priceValue) * PERCENTAGE_BASE
            )
          )
        : "";

    methods.reset({
      ...draft,
      listingType:
        draft.listingType != null ? String(draft.listingType) : undefined,
      depositPercentage,
      currencyId: draft.currencyId || DEFAULT_CURRENCY_ID,
    });
    setDraftId(draft.id);
    setHasDraft(true);
    setDeletedImages([]);
    const coverIndex = (draft.images ?? []).findIndex((img) => img.type === 1);
    setCoverImageIndex(coverIndex >= ZERO_VALUE ? coverIndex : ZERO_VALUE);
    setCoverImageId(draft.images?.find((img) => img.type === 1)?.id);
    setSaveChanges(false);
  };

  const performSaveDraft = () => {
    const data = methods.getValues();
    const formData = buildPropertyFormData(data, { asDraft: true });

    savePropertyDraft(formData, (savedDraft) => {
      if (savedDraft?.id) {
        setDraftId(savedDraft.id);
      }
      setHasDraft(true);
      setShowDraftOverwritePrompt(false);
      // Reload draft so uploaded Files become id/url and are not re-uploaded
      // on the next Save Draft.
      getPropertyDraft(
        (draft) => {
          applyDraftToForm(draft);
        },
        () => {
          setSaveChanges(false);
        }
      );
    });
  };

  const handleSaveDraft = () => {
    // Always confirm when a draft already exists (including after retrieve).
    if (hasDraft) {
      setShowDraftOverwritePrompt(true);
      return;
    }
    performSaveDraft();
  };

  const handleRetrieveDraft = () => {
    getPropertyDraft(
      (draft) => {
        applyDraftToForm(draft);
      },
      () => {
        setHasDraft(false);
        customToast("No draft found", ERROR_TOAST);
      },
      true
    );
  };

  useEffect(() => {
    // Check if any of the specified error keys exist in formState.errors
    const hasErrors = firstPageErrorKeys.some(
      (key) => key in methods.formState.errors
    );

    if (hasErrors) {
      changeFormType(TYPE_DETAILS);
    }
    // eslint-disable-next-line
  }, [methods.formState.errors]);

  useEffect(() => {
    if (Object.entries(propertyDetail).length > 0 && id) {
      const priceValue = Number(propertyDetail.price);
      const depositValue = Number(propertyDetail.depositAmount);
      const depositPercentage =
        Number.isFinite(priceValue) &&
        priceValue > ZERO_VALUE &&
        Number.isFinite(depositValue)
          ? Number(
              formatToTwoDecimalPlaces(
                (depositValue / priceValue) * PERCENTAGE_BASE
              )
            )
          : "";

      methods.reset({
        ...propertyDetail,
        listingType: String(propertyDetail.listingType),
        depositPercentage,
      });
      setCoverImageIndex(
        propertyDetail.images.findIndex((img) => img.type === 1)
      );
      setCoverImageId(propertyDetail.images.find((img) => img.type === 1)?.id);
    }

    // eslint-disable-next-line
  }, [propertyDetail]);

  useEffect(() => {
    if (id) {
      getPropertyDetailById(id);
    }

    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    getAllMustHaveAmmenities();
    getAllPropertyTypes();
    getAllViewTypes();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id) {
      return;
    }

    getPropertyDraft(
      () => {
        setHasDraft(true);
      },
      () => {
        setHasDraft(false);
      }
    );
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    const subscription = methods.watch(() => {
      setSaveChanges(true);
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  return (
    <>
      <Topbar />
      <MainContentWrapper>
        {(spinner(propertyConstants.CREATE_PROPERTY) > -1 ||
          spinner(propertyConstants.GET_PROPERTY_DETAIL_BY_ID) > -1 ||
          spinner(propertyConstants.SAVE_PROPERTY_DRAFT) > -1 ||
          spinner(propertyConstants.GET_PROPERTY_DRAFT) > -1) && (
          <MainSpinner />
        )}
        {showDialogPrompt && (
          <ConfirmationPopup
            onCancel={cancelNavigation}
            onConfirm={() => {
              confirmNavigation();
              setSaveChanges(false);
            }}
          />
        )}
        {showDraftOverwritePrompt && (
          <ConfirmationPopup
            title={DRAFT_OVERWRITE_TITLE}
            message={DRAFT_OVERWRITE_MESSAGE}
            onCancel={() => setShowDraftOverwritePrompt(false)}
            onConfirm={performSaveDraft}
          />
        )}
        <div className="agent-create-listing">
          <div className="container">
            <div className="create-list-form">
              <div className="create-list-form-heading">
                {id ? "Update Listing" : "Create a New Listing"}
              </div>
              <div className="form-type d-flex justify-content-center">
                <div
                  className={`pointer text-center ${
                    formType === TYPE_DETAILS ? "selected-form-type" : ""
                  }`}
                  onClick={() => changeFormType(TYPE_DETAILS)}
                >
                  Ad Details
                </div>
                <div
                  className={`pointer text-center ${
                    formType === TYPE_FEATURES ? "selected-form-type" : ""
                  }`}
                  onClick={() => changeFormType(TYPE_FEATURES)}
                >
                  Features, Amenities and Photos
                </div>
              </div>
              <FormProvider {...methods}>
                <FeaturesModal
                  show={openFeatureModal}
                  onClose={handleCloseFeatureModal}
                />

                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  {formType === TYPE_DETAILS ? (
                    <>
                      <ListingTypeCard />
                      <PriceAndArea />
                      <AdInformation />
                      <PropertyTypes />
                      <ViewTypes />
                      <AddressAndLocation />
                      <div className="d-flex justify-content-center flex-wrap gap-3">
                        {!id && (
                          <>
                            <RoundedButton
                              cotainerClass="mt-4"
                              onClick={handleSaveDraft}
                              variant="outlined-blue"
                              className="px-lg-5 py-2"
                              type="button"
                            >
                              Save Draft
                            </RoundedButton>
                            {hasDraft && (
                              <RoundedButton
                                cotainerClass="mt-4"
                                onClick={handleRetrieveDraft}
                                variant="outlined-blue"
                                className="px-lg-5 py-2"
                                type="button"
                              >
                                Retrieve Draft
                              </RoundedButton>
                            )}
                          </>
                        )}
                        <RoundedButton
                          cotainerClass="mt-4"
                          className="next-button"
                          onClick={() => changeFormType(TYPE_FEATURES)}
                          type="button"
                        >
                          Next
                        </RoundedButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <FeaturesAndAmenities
                        handleOpenFeatureModal={handleOpenFeatureModal}
                      />
                      <PropertyImages
                        coverImageIndex={coverImageIndex}
                        setCoverImageIndex={setCoverImageIndex}
                        setDeletedImages={setDeletedImages}
                        setCoverImageId={setCoverImageId}
                      />
                      <div className="d-flex justify-content-center flex-wrap gap-3">
                        <RoundedButton
                          cotainerClass="mt-4"
                          onClick={() => changeFormType(TYPE_DETAILS)}
                          variant="outlined-blue"
                          className="px-lg-5 py-2"
                          type="button"
                        >
                          Go Back
                        </RoundedButton>
                        {!id && (
                          <>
                            <RoundedButton
                              cotainerClass="mt-4"
                              onClick={handleSaveDraft}
                              variant="outlined-blue"
                              className="px-lg-5 py-2"
                              type="button"
                            >
                              Save Draft
                            </RoundedButton>
                            {hasDraft && (
                              <RoundedButton
                                cotainerClass="mt-4"
                                onClick={handleRetrieveDraft}
                                variant="outlined-blue"
                                className="px-lg-5 py-2"
                                type="button"
                              >
                                Retrieve Draft
                              </RoundedButton>
                            )}
                          </>
                        )}
                        <RoundedButton
                          cotainerClass="mt-4"
                          className="next-button submit-button"
                          type="submit"
                        >
                          {id ? "Update" : "Submit"}
                        </RoundedButton>
                      </div>
                    </>
                  )}
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </MainContentWrapper>
      <Footer />
    </>
  );
};

const mapStateToProps = (state) => ({
  saveChanges: state.systemReducer.saveChanges,
  spinnerActions: state.systemReducer.spinnerActions,
  propertyDetail: state.propertyReducer.propertyDetail,
});

const mapDispatchToProps = (dispatch) => ({
  createProperty: (data, func) =>
    dispatch(propertyActions.createProperty(data, func)),
  updateProperty: (data, func) =>
    dispatch(propertyActions.updateProperty(data, func)),
  savePropertyDraft: (data, func) =>
    dispatch(propertyActions.savePropertyDraft(data, func)),
  getPropertyDraft: (successFunc, emptyFunc, showRetrievedToast) =>
    dispatch(
      propertyActions.getPropertyDraft(
        successFunc,
        emptyFunc,
        showRetrievedToast
      )
    ),
  getPropertyDetailById: (id) =>
    dispatch(propertyActions.getPropertyDetailById(id)),
  getAllMustHaveAmmenities: () =>
    dispatch(homeActions.getAllMustHaveAmmenities()),
  getAllPropertyTypes: () => dispatch(propertyActions.getAllPropertyTypes()),
  getAllViewTypes: () => dispatch(propertyActions.getAllViewTypes()),
  setSaveChanges: (flag) => dispatch(systemActions.setSaveChanges(flag)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateListing);
